/**
 * Filter drawer helpers (collection + search pages). Never changes what Shopify filters.
 *
 * 1. Find-in-list ([data-filter-find]) for long filter lists such as Vendor. It only shows/hides
 *    rows already rendered by Shopify. Its events are stopped in the capture phase so the search
 *    page's live form does not treat typing as a filter change. "kenele" matches both
 *    "Ken Elephant" and "Kenelephant" (spaces/punctuation ignored).
 * 2. Collection page: closing the drawer without "Show results" puts every control back to the
 *    applied state, so reopening shows exactly the active filters and a later sort change can't
 *    submit stray, unapplied ticks. (Search applies each tick live, so nothing to revert there.)
 */
const squash = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
const lastQueries = new Map();

function applyFind(input) {
  const fieldset = input.closest('fieldset');
  if (!fieldset) return;
  const raw = input.value.trim().toLowerCase();
  const query = squash(raw);
  if (input.id) lastQueries.set(input.id, input.value);
  let shown = 0;
  for (const row of fieldset.querySelectorAll('li')) {
    const text = row.textContent || '';
    const match = !query || text.toLowerCase().includes(raw) || squash(text).includes(query);
    row.hidden = !match;
    if (match) shown += 1;
  }
  // While searching, reveal values tucked behind "Show more" and hide that toggle.
  for (const more of fieldset.querySelectorAll('details')) {
    if (query) {
      if (!more.dataset.findWasOpen) more.dataset.findWasOpen = more.open ? '1' : '0';
      more.open = true;
    } else if (more.dataset.findWasOpen) {
      more.open = more.dataset.findWasOpen === '1';
      delete more.dataset.findWasOpen;
    }
    const summary = more.querySelector(':scope > summary');
    if (summary) summary.hidden = Boolean(query);
  }
  const empty = fieldset.querySelector('[data-filter-find-empty]');
  if (empty) empty.hidden = !query || shown > 0;
}

const isFind = (target) => target instanceof HTMLInputElement && target.matches('[data-filter-find]');

document.addEventListener('input', (event) => {
  if (!isFind(event.target)) return;
  event.stopPropagation();
  applyFind(event.target);
}, true);

document.addEventListener('change', (event) => {
  if (isFind(event.target)) event.stopPropagation();
}, true);

document.addEventListener('keydown', (event) => {
  if (isFind(event.target) && event.key === 'Enter') {
    event.preventDefault();
    event.stopPropagation();
  }
}, true);

// Live search updates replace the list; keep the shopper's find text.
new MutationObserver(() => {
  for (const [id, value] of lastQueries) {
    const input = document.getElementById(id);
    if (input instanceof HTMLInputElement && isFind(input) && input.value !== value) {
      input.value = value;
      applyFind(input);
    }
  }
}).observe(document.body, { childList: true, subtree: true });

function resetToApplied(dialog) {
  for (const control of dialog.querySelectorAll('input')) {
    if (isFind(control)) {
      control.value = '';
      if (control.id) lastQueries.delete(control.id);
      applyFind(control);
    } else if (control.type === 'checkbox' || control.type === 'radio') {
      control.checked = control.defaultChecked;
    } else if (control.type === 'number' || control.type === 'text') {
      control.value = control.defaultValue;
    }
  }
}

for (const host of document.querySelectorAll('[data-filter-drawer]')) {
  const dialog = host.querySelector('dialog');
  if (!dialog || !host.closest('s-collection-form')) continue;
  let submitting = false;
  host.closest('form')?.addEventListener('submit', () => { submitting = true; });
  dialog.addEventListener('close', () => {
    if (!submitting) resetToApplied(dialog);
  });
  window.addEventListener('pageshow', () => { submitting = false; });
}
